import { OptionValue } from "@app/shared";
import { forwardRef, useEffect, useState } from "react";
import { Input, InputProps } from ".";

type InputWithOptionsProps = {
    options: OptionValue[];
    value: string | undefined;
    onChange: (newValue: string | undefined) => void;
} & Omit<InputProps, 'value' | 'onChange'>;

const renderOptions = (options: OptionValue[], onClick: (value: OptionValue) => void) =>
    <ul className="divide-y divide-page divide-opacity-30 border-page-border border mb-3 max-h-60 overflow-y-auto">
        {options.map((option) => {
            return (
                <li key={option.value} onClick={() => onClick(option)} className="w-full p-2 cursor-pointer flex hover:bg-background-page-highlight-gray hover:bg-opacity-50">
                    <div className="flex-grow">
                        {option.label}
                    </div>
                    <div className="mx-2 text-page-link">
                        select
                    </div>
                </li>
            );
        })}
    </ul>

export const InputWithOptions = forwardRef<HTMLInputElement, InputWithOptionsProps>(
    ({ onChange, value, options, ...props }, ref) => {
        const [filteredOptions, setFilteredOptions] = useState<OptionValue[]>([]);
        const [labelFilter, setLabelFilter] = useState<string>("");
        const [currentValue, setCurrentValue] = useState<OptionValue | undefined>();
        const [tbHasFocus, setTbHasFocus] = useState(false);
        const [optionsHasFocus, setOptionsHasFocus] = useState(false);

        const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
            //Reduce options by the entered text
            setLabelFilter(e.target.value);

            if (e.target.value !== currentValue?.label) {
                setCurrentValue(undefined);
                onChange(undefined);
            }
        };
        //If they click an option, set our current value
        const handleOptionClick = (value: OptionValue) => {
            setLabelFilter(value.label ?? "");
            setCurrentValue(value);
            onChange(value?.value);
        };

        useEffect(() => {
            setFilteredOptions(options.filter((option) => {
                if (!labelFilter) return true;
                return option.label?.toLowerCase().includes(labelFilter.toLowerCase());
            }
            ));
        }, [labelFilter, options]);

        useEffect(() => {
            //If we have a value, set the label filter to the value
            if (value) {
                const option = options.find((option) => option.value === value);
                if (option && currentValue?.value !== option?.value) {
                    setLabelFilter(option.label ?? "");
                    setCurrentValue(option);
                }
            } else {
                if (currentValue !== undefined) {
                    setLabelFilter("");
                    setCurrentValue(undefined);
                }
            }
        }, [value, options]); // eslint-disable-line react-hooks/exhaustive-deps

        const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
            setTbHasFocus(true);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
            setTbHasFocus(false);
            //If we do not have a value, clear the label filter
            if (!currentValue && !optionsHasFocus) {
                setLabelFilter("");
            }
        };

        const handleOptionMouseOver: React.MouseEventHandler<HTMLDivElement> = (e) => {
            setOptionsHasFocus(true);
        };

        const handleOptionsMouseLeave: React.MouseEventHandler<HTMLDivElement> = (e) => {
            setOptionsHasFocus(false);
            if (!currentValue && !tbHasFocus) {
                setLabelFilter("");
            }
        };

        const handleClear: React.MouseEventHandler = (e) => {
            e.preventDefault();
            setLabelFilter("");
            setCurrentValue(undefined);
            onChange(undefined);
        };

        const clearButton = currentValue ? (
            <button
                type="button"
                onMouseDown={handleClear}

                tabIndex={-1}
            >
                X
            </button>
        ) : undefined;

        return (<>
            <Input {...props} ref={ref} onChange={handleInputChange} value={labelFilter} onFocus={handleFocus} onBlur={handleBlur} addon={clearButton ?? props.addon} />
            {!currentValue && (tbHasFocus || optionsHasFocus) &&
                <div onMouseOver={handleOptionMouseOver} onMouseLeave={handleOptionsMouseLeave}>
                    {renderOptions(filteredOptions, handleOptionClick)}
                </div>
            }
        </>
        );
    }
);